import type { Preferences } from 'api/types/preferences.types';
import type { ServiceLine } from 'api/types/service.types';
import cx from 'classnames';
import { selectPreferenceType } from 'pages/newOrder/newOrder.selectors';
import React from 'react';
import Col from 'react-bootstrap/Col';
import ListGroup from 'react-bootstrap/ListGroup';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from 'redux-stores/reducers/rootReducer';
import styled from 'styled-components';

import type { IPreferencesOptions } from './OrderInstructionsOptions';
import styles from './styles.module.scss';

const StyledH4 = styled.h4`
  color: ${({ theme }) => theme.components.tipBox.text.color.unselected};
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
`;

interface MapperProps {
  title?: string;
  clickHandler(key: keyof Preferences.CustomerPreferences): any;
  mapperObj: {
    [key in keyof IPreferencesOptions]?: JSX.Element;
  };
  serviceLine?: ServiceLine;
}

const OptionsRenderer: React.FC<MapperProps> = ({ title, clickHandler, mapperObj, serviceLine }) => {
  const { t } = useTranslation();
  const foldingPreference = useSelector((state: RootState) => selectPreferenceType(state, 'folding', serviceLine));
  const creasesPreference = useSelector((state: RootState) => selectPreferenceType(state, 'creases', serviceLine));
  const starchPreference = useSelector((state: RootState) => selectPreferenceType(state, 'starch', serviceLine));
  const stainDamageApprovalsPreference = useSelector((state: RootState) => selectPreferenceType(state, 'stainDamageApprovals', serviceLine));
  const driverPickupInstructionsPreference = useSelector((state: RootState) => selectPreferenceType(state, 'driverPickupInstructions', serviceLine));
  const driverDropoffInstructionsPreference = useSelector((state: RootState) =>
    selectPreferenceType(state, 'driverDropoffInstructions', serviceLine)
  );

  const shoeAddOnApprovalsPreferences = useSelector((state: RootState) =>
    selectPreferenceType(state, 'shoeAddOnApprovals', serviceLine)
  ) as Preferences.ShoeAddOnApprovals;

  /**
   * @description for every preference we display an inconsistent value, that's why we handle each case differently. [Design needs consistency]
   * @param type
   */
  const getPreferenceValue = React.useCallback(
    (type: keyof IPreferencesOptions) => {
      switch (type) {
        case 'folding':
          return foldingPreference;
        case 'creases':
          return creasesPreference;
        case 'starch':
          return starchPreference;
        case 'stainDamageApprovals':
          return stainDamageApprovalsPreference;
        case 'driverPickupInstructions':
          return driverPickupInstructionsPreference;
        case 'driverDropoffInstructions':
          return driverDropoffInstructionsPreference;
        case 'restoration':
          return shoeAddOnApprovalsPreferences?.restoration ? t(`newOrderPage.restorationOption`) : undefined;
        case 'soleIcing':
          return shoeAddOnApprovalsPreferences?.soleIcing ? t(`newOrderPage.soleIcingOption`) : undefined;
        case 'stainProtection':
          return shoeAddOnApprovalsPreferences?.stainProtection ? t(`newOrderPage.stainProtectionOption`) : undefined;
        default:
          return undefined;
      }
    },
    [
      foldingPreference,
      creasesPreference,
      starchPreference,
      stainDamageApprovalsPreference,
      driverPickupInstructionsPreference,
      driverDropoffInstructionsPreference,
      shoeAddOnApprovalsPreferences?.restoration,
      shoeAddOnApprovalsPreferences?.soleIcing,
      shoeAddOnApprovalsPreferences?.stainProtection,
      t,
    ]
  );

  return (
    <>
      {title && (
        <Row>
          <Col>
            <StyledH4 className="font-weight-light mb-6">{title}</StyledH4>
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <ListGroup variant="flush">
            {Object.entries(mapperObj).map(([key, el]) => {
              if (el) {
                return React.cloneElement(el, {
                  key,
                  onClick: () => clickHandler(key as keyof Preferences.CustomerPreferences),
                  className: cx(el.props.className, styles.listItem),
                  value: getPreferenceValue(key as keyof Preferences.CustomerPreferences),
                });
              }
              return null;
            })}
          </ListGroup>
        </Col>
      </Row>
    </>
  );
};

export default OptionsRenderer;
